import React, { FC, useState } from 'react';
import { Alert, Spin } from 'antd';
import moment from 'moment';
import OWButton from '../ow_button/OWButton';
import LogRefrigerantEventDrawer from './LogRefrigerantEventDrawer';
import RefrigerantEventTypeDisplay from './RefrigerantEventTypeDisplay';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';

/**
 * The drawer has to land in the GAP between two of antd's layers, which is why this is 1001 and not a
 * comfortable round number above everything.
 *
 * antd's scale (`@zindex-*` in its default theme): modal and modal-mask are both 1000, `.ant-drawer`
 * is also 1000, and every POPUP the drawer's own fields open — `.ant-select-dropdown`,
 * `.ant-picker-dropdown` — is 1050. So the drawer must be:
 *
 *  - **above 1000**, or the check-out Modal it was opened from paints over it. Leaving it at the
 *    default 1000 makes the winner depend on the order the two portals happen to land in the body,
 *    which is not something to leave to chance.
 *  - **below 1050**, or the drawer panel paints over its own Select and DatePicker popups. That was
 *    the bug this constant originally introduced at 1100: the dropdowns opened, mounted, and were
 *    invisible behind the drawer, so the required Refrigerant / Technician / EPA fields could not be
 *    filled in at all.
 *
 * Tooltip (1070), popconfirm (1060) and popover (1030) are all above 1001 too, so they keep working.
 */
const DRAWER_Z_INDEX_ABOVE_MODAL = 1001;

interface CheckOutRefrigerantSectionProps {
	workOrder: any;
	/** The work order's refrigerant-tracked appliances, already resolved by the caller. */
	appliances: any[];
	/** The ledger rows that reference THIS work order. */
	events: any[];
	/**
	 * Each appliance's derived compliance summary, keyed by asset id.
	 *
	 * Handed to the log drawer as its `refrigerantSummary`, and that is not decoration: the summary is
	 * where the RESOLVED refrigerant lives. A unit that inherits its refrigerant from its asset type
	 * has `refrigerantTypeCode` null on its own row, so without this the drawer's Refrigerant field
	 * opens blank on exactly those appliances. It also powers the drawer's live leak-rate warning.
	 *
	 * A summary may legitimately be missing — its read is caught per appliance so it can never fail
	 * the gate — and the drawer then falls back to the asset row, which is the pre-existing behaviour.
	 */
	summaries: any;
	loading: boolean;
	/**
	 * The appliance or ledger read failed, so neither list can be trusted.
	 *
	 * Surfaced rather than swallowed, and deliberately NOT treated as "an event is required": see the
	 * fail-open note on the panel below.
	 */
	failed: boolean;
	/**
	 * Whether the gate is BLOCKING right now — a tracked appliance, no event, and a completing
	 * check-out status. Only the panel's severity, never whether it renders: a tech who has picked
	 * Parts on Order is not blocked but is still standing on an enrolled appliance, and the panel is
	 * where they find out.
	 */
	required: boolean;
	/** A newly logged event, for the caller to fold into `events` and re-validate against. */
	onLogged: (event: any) => void;
}

/**
 * One enrolled appliance on the job, with the action that opens the drawer for it.
 *
 * A component rather than a row rendered inline, so the per-appliance click handler can be a named
 * function closing over its OWN `appliance` prop. The alternative — an arrow in the `map` callback —
 * is the one shape that cannot be lifted out of the JSX, because the appliance it captures only
 * exists inside the iteration.
 */
const ApplianceRow: FC<{ appliance: any; onLogEvent: (appliance: any) => void }> = ({
	appliance,
	onLogEvent,
}) => {
	const handleLogEventClick = () => onLogEvent(appliance);

	return (
		<div className="flex flex-row items-center justify-between gap-2">
			<span className="bodyregular text-character-primary">
				{nullSafeGetOrElse('name', appliance, `Asset #${nullSafeGet('id', appliance)}`)}
			</span>
			<OWButton type="primary" ghost onClick={handleLogEventClick}>
				Log Refrigerant Event
			</OWButton>
		</div>
	);
};

/**
 * Refrigerant tracking, inside the check-out form.
 *
 * A sealed loop means refrigerant added to an appliance is refrigerant that leaked out of it, so the
 * servicing visit itself is the event the 608 record is built from — and the moment the tech is
 * closing the visit is the last moment anybody can still be asked for it. Hence the gate: a check-out
 * that completes a work order on an enrolled appliance has to carry a ledger row.
 *
 * PRESENTATIONAL, apart from the drawer it hosts. The check-out form owns the two reads and the
 * validation, because the same numbers have to drive the field error and this panel — deriving them
 * twice is how a form shows a green tick and refuses to submit.
 *
 * The panel is rendered for the whole gated population, not only for the blocked case: a tech who
 * already logged the event needs to SEE that, or the only feedback the feature ever gives is an error.
 */
export const CheckOutRefrigerantSection: FC<CheckOutRefrigerantSectionProps> = ({
	workOrder,
	appliances,
	events,
	summaries,
	loading,
	failed,
	required,
	onLogged,
}) => {
	// Which appliance the drawer is open for. A work order can name several enrolled units (a rack
	// with tracked sub-assets), and the drawer's own asset picker is scoped to the whole tracked
	// estate rather than to this work order — so the appliance is chosen HERE, from the ones actually
	// on the job, and handed over locked.
	const [applianceBeingLogged, setApplianceBeingLogged] = useState(null);

	const closeLogEventDrawer = () => setApplianceBeingLogged(null);

	// Closed BEFORE the event is handed up, so the drawer is unmounted by the time the caller folds the
	// row in and re-validates — the same order the inline handler ran in.
	const handleEventLogged = (event) => {
		setApplianceBeingLogged(null);
		onLogged(event);
	};

	/*
	  Every state below carries 24px of bottom margin — antd's `@form-item-margin-bottom`.

	  The panel sits directly above the Check Out Status field INSIDE the form, so it has to keep the
	  form's own field rhythm; without it the banner sat flush against the "Check Out Status" label and
	  read as part of that field rather than as its own block. Same value in all four states so the
	  field below does not shift as the panel resolves from spinner to alert.

	  `!mb-6` on the ALERTS, plain `mb-6` on the plain div: antd's own `.ant-alert { margin: 0 }` is
	  injected by babel-plugin-import AFTER tailwind.out.css, so at equal specificity the later rule
	  wins and an unprefixed `mb-*` on an `<Alert>` is a silent no-op. The important modifier outranks
	  the reset whatever order the two stylesheets land in. Nothing in antd competes for the div, so it
	  does not need the escape hatch. Same fix, same reason, as the notice banner in
	  `LogRefrigerantEventDrawer`.
	*/
	if (loading) {
		return (
			<div className="mb-6 flex flex-row items-center gap-2">
				<Spin size="small" />
				<span className="footnoteregular text-character-secondary">
					Checking refrigerant tracking...
				</span>
			</div>
		);
	}

	/*
	  FAIL OPEN, loudly.

	  A failed read means we do not know whether this work order needs an event — and the two ways to
	  be wrong are not symmetric. Failing closed makes a work order impossible to close for as long as
	  the request keeps failing, on evidence we do not have. Failing open costs at most one event
	  logged later than it should have been, from the appliance's own Refrigerant Tracking tab, into a
	  ledger that is append-only and accepts it.

	  Shown rather than swallowed for the same reason the asset tab says so: an empty compliance
	  section that is empty because the request failed reads as "nothing was required here".
	*/
	if (failed) {
		return (
			<Alert
				className="!mb-6"
				type="warning"
				showIcon
				message="Could not check refrigerant tracking"
				description="If this work order is on a refrigerant-tracked appliance, log the event from its Refrigerant Tracking tab after checking out."
			/>
		);
	}

	if (appliances.length === 0) return null;

	const hasEvents = events.length > 0;

	return (
		<>
			{hasEvents ? (
				<Alert
					className="!mb-6"
					type="success"
					showIcon
					message={`Refrigerant event${events.length > 1 ? 's' : ''} logged`}
					description={
						<div className="flex flex-col gap-2">
							{events.map((event) => (
								<div key={nullSafeGet('id', event)} className="flex flex-col">
									<RefrigerantEventTypeDisplay
										event={event}
										showLeakSource={false}
										showChargeReason={false}
										plain
									/>
									{/*
									  Date-only and in UTC, as everywhere else the ledger is read: `occurredAt` is
									  the DAY the appliance was serviced, and rendering it through the viewer's zone
									  would show a UTC-midnight event as the previous day.
									*/}
									<span className="footnoteregular text-character-secondary">
										{moment.utc(nullSafeGet('occurredAt', event)).format('D MMMM YYYY')}
									</span>
								</div>
							))}
						</div>
					}
				/>
			) : (
				<Alert
					className="!mb-6"
					type={required ? 'error' : 'warning'}
					showIcon
					message="Refrigerant event required"
					description={
						<div className="flex flex-col gap-2">
							<span>
								{appliances.length > 1
									? 'This work order is on refrigerant-tracked appliances. Log the servicing event before completing check out.'
									: 'This work order is on a refrigerant-tracked appliance. Log the servicing event before completing check out.'}
							</span>
							<div className="flex flex-col gap-2">
								{appliances.map((appliance) => (
									<ApplianceRow
										key={nullSafeGet('id', appliance)}
										appliance={appliance}
										onLogEvent={setApplianceBeingLogged}
									/>
								))}
							</div>
						</div>
					}
				/>
			)}
			{/*
			  Mounted only while open, so the drawer's `clientEventUuid` — its idempotency key, minted
			  once per opening — is genuinely fresh each time rather than carried over from a cancelled
			  attempt on a different appliance.
			*/}
			{applianceBeingLogged ? (
				<LogRefrigerantEventDrawer
					visible={true}
					zIndex={DRAWER_Z_INDEX_ABOVE_MODAL}
					asset={applianceBeingLogged}
					workOrder={workOrder}
					refrigerantSummary={
						summaries ? summaries[nullSafeGet('id', applianceBeingLogged)] : undefined
					}
					onCancel={closeLogEventDrawer}
					onSuccess={handleEventLogged}
				/>
			) : null}
		</>
	);
};

export default CheckOutRefrigerantSection;
